import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MagnifyingGlassIcon, MapPinIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const base = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

interface MunicipalityOption {
  id: string;
  name: string;
  code: string;
  logoUrl: string | null;
}

/**
 * Public, no-login landing page for citizens who land on the platform
 * without a direct link to their own municipality's status page. Lists
 * every active tenant (name/code/logo only — nothing sensitive) and lets
 * them search and jump straight to /service-status/:code.
 */
const FindMunicipality: React.FC = () => {
  const [municipalities, setMunicipalities] = useState<MunicipalityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    axios
      .get(`${base}/public/municipalities`)
      .then((r) => setMunicipalities(r.data?.data || []))
      .catch(() => setError("Couldn't load the list of municipalities. Please try again shortly."))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return municipalities;
    return municipalities.filter(
      (m) => m.name.toLowerCase().includes(query) || m.code.toLowerCase().includes(query)
    );
  }, [municipalities, q]);

  return (
    <main className="min-h-screen bg-slate-50">
      <header className="bg-blue-900 px-6 py-10 text-white">
        <div className="mx-auto max-w-2xl">
          <h1 className="text-3xl font-bold">Find your municipality</h1>
          <p className="mt-1 text-blue-100">
            Search for your municipality to view its public service status — reported issues, response
            promises, and current disruptions.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-2xl p-6">
        <div className="relative mb-6">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by municipality name…"
            aria-label="Search for your municipality"
            className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-base shadow-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
            autoFocus
          />
        </div>

        {loading ? (
          <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">Loading municipalities…</p>
        ) : error ? (
          <p className="rounded-lg bg-red-50 p-6 text-center text-sm text-red-700 shadow-sm">{error}</p>
        ) : filtered.length === 0 ? (
          <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">
            {municipalities.length === 0
              ? 'No municipalities are set up yet.'
              : `No municipality matches "${q}".`}
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => (
              <Link
                key={m.id}
                to={`/service-status/${m.code}`}
                className="flex items-center justify-between gap-3 rounded-lg bg-white p-4 shadow-sm transition-colors hover:bg-blue-50"
              >
                <div className="flex items-center gap-3">
                  {m.logoUrl ? (
                    <img src={m.logoUrl} alt="" className="h-10 w-10 rounded object-contain" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-gray-900">{m.name}</div>
                    <div className="text-xs text-gray-500">{m.code}</div>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              </Link>
            ))}
          </div>
        )}

        <p className="mt-8 text-center text-sm text-gray-500">
          Municipal staff looking to sign in?{' '}
          <Link to="/login" className="font-medium text-blue-700 hover:text-blue-800">
            Go to Admin Portal Login
          </Link>
        </p>
      </div>
    </main>
  );
};

export default FindMunicipality;
